export type NavigationNode = {
  id: string
  label: string
  icon?: string
  to?: string
  permissions?: string[]
  children?: NavigationNode[]
}

export type NavigationSection = {
  id: string
  label: string
  nodes: NavigationNode[]
}

const providerNode: NavigationNode = {
  id: 'providers',
  label: 'Provedores',
  icon: 'PR',
  children: [
    { id: 'provider-list', to: '/admin/providers', label: 'Configurações', icon: 'PC', permissions: ['providers.read'] },
    { id: 'provider-models', to: '/admin/providers/models', label: 'Modelos e capacidades', icon: 'MC', permissions: ['providers.read'] },
    { id: 'provider-tokens', to: '/admin/tokens', label: 'Tokens e custos', icon: '$', permissions: ['tokens.read'] },
  ],
}

const agentNode: NavigationNode = {
  id: 'agents',
  to: '/admin/agents',
  label: 'Agentes',
  icon: 'AG',
  permissions: ['agents.read'],
}

const brainNodes: NavigationNode[] = [
  { id: 'brain-operational', to: '/admin/brain-operational', label: 'Painel Operacional', icon: 'PO', permissions: ['brain.read', 'reports.read'] },
  {
    id: 'orchestration',
    to: '/admin/brain',
    label: 'Orquestração de IA',
    icon: 'OI',
    permissions: ['brain.read'],
    children: [providerNode, agentNode],
  },
  { id: 'channels', to: '/admin/channels', label: 'Canais', icon: 'CH', permissions: ['channel.read'] },
  { id: 'whatsapp-flow', label: 'MIA WhatsApp Flow', icon: 'WF', children: [
    { id: 'whatsapp-flow-connections', to: '/admin/whatsapp-flow', label: 'Conexões', icon: 'CX', permissions: ['whatsapp_flow.view', 'tenant.manage'] },
    { id: 'whatsapp-flow-meta-templates', to: '/admin/whatsapp-flow/templates', label: 'Meta Templates', icon: 'MT', permissions: ['whatsapp_flow.templates.view'] },
  ] },
  {
    id: 'applications',
    label: 'Aplicações',
    icon: 'AP',
    children: [
      { id: 'prospect-ai', to: '/admin/prospect-sdr', label: 'MIA Prospect AI', icon: 'SD', permissions: ['prospect_sdr.read', 'tenant.manage'] },
      { id: 'every-styles', to: '/admin/applications/mia-every-styles', label: 'MIA Every Styles', icon: 'ES', permissions: ['catalog.read', 'sessions.read'] },
    ],
  },
]

export const navigationSections: NavigationSection[] = [
  {
    id: 'platform',
    label: 'Plataforma',
    nodes: [
      { id: 'dashboard', to: '/admin', label: 'Dashboard', icon: 'DB', permissions: ['reports.read'] },
      { id: 'tenants', to: '/admin/tenants', label: 'Tenants', icon: 'TN', permissions: ['tenant.manage'] },
      { id: 'environments', to: '/admin/environments', label: 'Ambientes', icon: 'AM', permissions: ['tenant.manage'] },
      { id: 'users', to: '/admin/users', label: 'Usuários', icon: 'US', permissions: ['users.read', 'users.manage'] },
      { id: 'roles', to: '/admin/roles', label: 'Papéis', icon: 'PP', permissions: ['roles.read', 'roles.manage'] },
    ],
  },
  { id: 'brain', label: 'Cérebro da MIA', nodes: brainNodes },
  {
    id: 'configuration',
    label: 'Configuração da plataforma',
    nodes: [
      { id: 'configurations', to: '/admin/configurations', label: 'Configurações', icon: 'CF', permissions: ['security.manage'] },
      { id: 'feature-flags', to: '/admin/feature-flags', label: 'Feature flags', icon: 'FF', permissions: ['security.manage'] },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrações',
    nodes: [
      { id: 'api-routes', to: '/admin/routes', label: 'Rotas de API', icon: 'RT', permissions: ['routes.read'] },
      { id: 'integrations-page', to: '/admin/integrations', label: 'Integrações', icon: 'IN', permissions: ['providers.read'] },
    ],
  },
  {
    id: 'governance',
    label: 'Governança',
    nodes: [
      { id: 'secrets', to: '/admin/secrets', label: 'Secrets', icon: 'SC', permissions: ['channel.read'] },
      { id: 'api-keys', to: '/admin/api-keys', label: 'Chaves de API', icon: 'AK', permissions: ['security.manage'] },
      { id: 'deleted', to: '/admin/deleted-resources', label: 'Lixeira', icon: 'LX', permissions: ['security.manage'] },
      { id: 'audit', to: '/admin/audit', label: 'Auditoria', icon: 'AU', permissions: ['audit.read'] },
      { id: 'security', to: '/admin/security', label: 'Segurança', icon: 'SG', permissions: ['security.manage'] },
    ],
  },
]

export function canAccessNode(node: NavigationNode, permissions: string[]) {
  if (permissions.includes('*') || !node.permissions?.length) {
    return true
  }
  return node.permissions.some((permission) => permissions.includes(permission))
}

export function visibleNavigation(sections: NavigationSection[], permissions: string[]): NavigationSection[] {
  const filterNode = (node: NavigationNode): NavigationNode | null => {
    const accessible = canAccessNode(node, permissions)
    if (node.children) {
      const children = node.children.map(filterNode).filter((child): child is NavigationNode => Boolean(child))
      const canNavigateToNode = Boolean(node.to && accessible)
      if (!children.length && !canNavigateToNode) {
        return null
      }
      return {
        ...node,
        to: canNavigateToNode ? node.to : undefined,
        children: children.length ? children : undefined,
      }
    }
    if (!accessible) {
      return null
    }
    return node
  }
  return sections.map((section) => ({ ...section, nodes: section.nodes.map(filterNode).filter((node): node is NavigationNode => Boolean(node)) })).filter((section) => section.nodes.length > 0)
}

type NavigationTrailMatch = {
  labels: string[]
  length: number
}

function selectLongerTrail(current: NavigationTrailMatch | null, candidate: NavigationTrailMatch | null): NavigationTrailMatch | null {
  if (!candidate) {
    return current
  }
  if (!current || candidate.length > current.length) {
    return candidate
  }
  return current
}

function matchesNavigationPath(pathname: string, target: string): boolean {
  if (pathname === target) {
    return true
  }
  return target !== '/admin' && pathname.startsWith(`${target}/`)
}

function findNavigationTrail(pathname: string, nodes: NavigationNode[], labels: string[]): NavigationTrailMatch | null {
  let best: NavigationTrailMatch | null = null
  for (const node of nodes) {
    const nextLabels = [...labels, node.label]
    if (node.to && matchesNavigationPath(pathname, node.to)) {
      best = selectLongerTrail(best, { labels: nextLabels, length: node.to.length })
    }
    if (node.children?.length) {
      best = selectLongerTrail(best, findNavigationTrail(pathname, node.children, nextLabels))
    }
  }
  return best
}

export function navigationTrail(pathname: string, sections: NavigationSection[]) {
  let best: NavigationTrailMatch | null = null
  for (const section of sections) {
    best = selectLongerTrail(best, findNavigationTrail(pathname, section.nodes, [section.label]))
  }
  return best?.labels ?? []
}
